import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { getApiErrorMessage, getModels } from '../api'
import FanslyFypManager from '../components/FanslyFypManager'
import { asList, modelTitle, providerLinks, providerStatus } from './v2/v2Utils'
import '../styles/pages/fansly-fyp-page.css'

function connectedFanslyLink(model) {
  return providerLinks(model).find((link) => (
    String(link?.provider || '').toLowerCase() === 'fansly'
    && providerStatus(link) === 'connected'
  ))
}

export default function FanslyFypPage({ variant = 'classic' }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [models, setModels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    getModels()
      .then((result) => { if (alive) setModels(asList(result)) })
      .catch((err) => { if (alive) setError(getApiErrorMessage(err, 'Failed to load Fansly accounts')) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const fanslyModels = useMemo(
    () => models.filter((model) => connectedFanslyLink(model)),
    [models],
  )
  const requestedModelId = Number(searchParams.get('model'))
  const selectedModel = fanslyModels.find((model) => Number(model.id) === requestedModelId)
    || fanslyModels[0]

  useEffect(() => {
    if (!selectedModel || Number(selectedModel.id) === requestedModelId) return
    setSearchParams({ model: String(selectedModel.id) }, { replace: true })
  }, [requestedModelId, selectedModel, setSearchParams])

  function selectModel(event) {
    setSearchParams({ model: event.target.value })
  }

  const link = selectedModel ? connectedFanslyLink(selectedModel) : null
  const connectionsPath = variant === 'v2' ? '/v2/connections' : '/old/models'

  return (
    <div className={`fyp-product-page fyp-product-page--${variant}`}>
      <header className="fyp-product-head">
        <div>
          <p>Publishing</p>
          <h2>FYP Publisher</h2>
        </div>
        {selectedModel ? (
          <label className="fyp-product-model-select">
            <span>Fansly account</span>
            <select value={selectedModel.id} onChange={selectModel}>
              {fanslyModels.map((model) => (
                <option key={model.id} value={model.id}>{modelTitle(model)}</option>
              ))}
            </select>
          </label>
        ) : null}
      </header>

      {loading ? <div className="fyp-product-status">Loading Fansly accounts...</div> : null}
      {error ? <div className="fyp-product-status is-error">{error}</div> : null}
      {!loading && !error && !selectedModel ? (
        <div className="fyp-product-empty">
          <strong>No connected Fansly accounts</strong>
          <Link to={connectionsPath}>Open connections</Link>
        </div>
      ) : null}

      {selectedModel ? (
        <>
          <div className="fyp-product-account-bar">
            <div>
              <span>Active workspace</span>
              <strong>{modelTitle(selectedModel)}</strong>
            </div>
            <div>
              <span>Provider</span>
              <strong>Fansly</strong>
            </div>
            <div>
              <span>Connection</span>
              <strong className="is-connected">Connected</strong>
            </div>
            <div>
              <span>Account ID</span>
              <strong>{link?.external_id || link?.account_id || `Model #${selectedModel.id}`}</strong>
            </div>
          </div>
          <FanslyFypManager key={selectedModel.id} modelId={selectedModel.id} variant={variant} />
        </>
      ) : null}
    </div>
  )
}
